import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  RoundingConfigurationResponse,
  BasedOnType,
  RoundingPolicy,
  AggregationRoundingPolicy,
  RoundingSettings
} from 'src/app/core/interfaces/rounding-settings.interface';
import { environment } from 'src/environments/environment';
import { JwtService } from 'src/app/core/services/jwt.service';

@Injectable({
  providedIn: 'root'
})
export class RoundingSettingsService {
  private baseUrl = environment.apiBaseUrl;
  private readonly ROUNDING_SETTINGS_KEY = 'roundingSettings';

  // Current rounding settings observable
  private currentRoundingSettings$ = new BehaviorSubject<RoundingSettings | null>(null);

  constructor(
    private http: HttpClient,
    private jwtService: JwtService
  ) {
    // Initialize with settings from localStorage if available
    this.initializeFromStorage();
    // Listen for storage changes from other tabs
    this.setupCrossTabSync();
  }

  /**
   * Centralized rounding method for the entire system.
   * Handles policy-based rounding (Up, Down, Nearest) with configurable precision.
   */
  public round(value: number | string | undefined | null, customSettings?: Partial<RoundingSettings>): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    let numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) {
      return 0;
    }

    // Normalization: round to a very high precision (12 decimals) to eliminate 
    // JS floating point noise (e.g., 12.017499999999999 -> 12.0175)
    numValue = Math.round(numValue * 1e12) / 1e12;

    const settings = this.getCurrentRoundingSettings();
    const precision = customSettings?.displayPrecision ?? settings?.displayPrecision ?? 2;
    const policy = customSettings?.roundingPolicy ?? settings?.roundingPolicy ?? RoundingPolicy.Nearest;
    const tolerance = customSettings?.roundingTolerance ?? settings?.roundingTolerance ?? 0;

    const factor = Math.pow(10, precision);
    
    // Apply tolerance: if the distance to nearest rounded value is within tolerance, use nearest
    if (tolerance > 0) {
      const nearest = Math.round((numValue + Number.EPSILON) * factor) / factor;
      if (Math.abs(numValue - nearest) <= tolerance) {
        return nearest;
      }
    }

    let roundedValue: number;
    switch (policy) {
      case RoundingPolicy.Up:
        roundedValue = Math.ceil(numValue * factor) / factor;
        break;
      case RoundingPolicy.Down:
        roundedValue = Math.floor(numValue * factor) / factor;
        break;
      case RoundingPolicy.Nearest:
      default:
        // Use Number.EPSILON to avoid floating point issues with Math.round
        roundedValue = Math.round((numValue + Number.EPSILON) * factor) / factor;
        break;
    }

    return roundedValue;
  }

  /**
   * API contract: fixed policy fields + user `displayPrecision` (see PUT body shape).
   */
  applyLockedRoundingDefaults(
    settings?: Partial<RoundingSettings> | null,
  ): RoundingSettings {
    const raw = settings?.displayPrecision;
    let displayPrecision = 2;
    if (raw !== null && raw !== undefined && `${raw}` !== '') {
      const n = Number(raw);
      if (Number.isFinite(n)) {
        displayPrecision = Math.min(6, Math.max(0, Math.trunc(n)));
      }
    }
    return {
      displayPrecision,
      roundingPolicy: RoundingPolicy.Nearest,
      aggregationRoundingPolicy: AggregationRoundingPolicy.RoundThenAggregate,
      roundingTolerance: 0,
      showStorageHint: true,
      autoRecalculateOnPriceChange: true,
    };
  }

  /**
   * Refreshes rounding settings from the API for a specific branch and caches them.
   */
  public refreshSettings(branchId: number): Observable<RoundingConfigurationResponse> {
    // 1. Immediately load from cache for this branch to prevent "flicker" of default 2
    const cachedSettings = this.getRoundingSettingsFromStorage(branchId);
    if (cachedSettings) {
      this.currentRoundingSettings$.next(cachedSettings);
    }

    // 2. Fetch fresh settings from API
    return this.getRoundingConfiguration(BasedOnType.Branch, branchId).pipe(
      tap((response: RoundingConfigurationResponse) => {
        if (response?.data?.roundingSettings) {
          const merged = this.applyLockedRoundingDefaults(
            response.data.roundingSettings,
          );
          this.saveRoundingSettingsToStorage(merged, branchId);
        }
      })
    );
  }

  getRoundingConfiguration(
    basedOnType: BasedOnType, 
    basedOnId: number
    ): Observable<RoundingConfigurationResponse> {
    return this.http.get<RoundingConfigurationResponse>(
      `${this.baseUrl}/Setting/rounding/${basedOnType}/${basedOnId}`
    );
  }

  // Save rounding settings to localStorage
  saveRoundingSettingsToStorage(settings: RoundingSettings, branchId?: number): void {
    try {
      const tenantId = this.getCurrentTenantId();
      
      // If branchId is provided, save with branch-specific key
      if (branchId) {
        const branchKey = `${tenantId}_${this.ROUNDING_SETTINGS_KEY}_branch_${branchId}`;
        localStorage.setItem(branchKey, JSON.stringify(settings));
      }
      
      // Always save to the global key as "last used" settings
      const globalKey = `${tenantId}_${this.ROUNDING_SETTINGS_KEY}`;
      localStorage.setItem(globalKey, JSON.stringify(settings));

      this.currentRoundingSettings$.next(settings);
      // Broadcast to other tabs
      this.broadcastSettingsChange(settings);
    } catch (error) {
      // Failed to save to localStorage
    }
  }

  private getCurrentTenantId(): string {
    // Switch to localStorage because sessionStorage is not shared between tabs
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.tenantId || '1';
  }

  // Save rounding settings to localStorage (alias for cacheRoundingSettings)
  cacheRoundingSettings(basedOnType: BasedOnType, basedOnId: number, settings: RoundingSettings): void {
    const branchId = basedOnType === BasedOnType.Branch ? basedOnId : undefined;
    this.saveRoundingSettingsToStorage(settings, branchId);
  }

  // Get rounding settings from localStorage
  getRoundingSettingsFromStorage(branchId?: number): RoundingSettings | null {
    try {
      const tenantId = this.getCurrentTenantId();
      
      // Try branch-specific key first if branchId provided
      if (branchId) {
        const branchKey = `${tenantId}_${this.ROUNDING_SETTINGS_KEY}_branch_${branchId}`;
        const branchStored = localStorage.getItem(branchKey);
        if (branchStored) {
          return JSON.parse(branchStored);
        }
      }
      
      // Fallback to global/last-used key
      const globalKey = `${tenantId}_${this.ROUNDING_SETTINGS_KEY}`;
      const globalStored = localStorage.getItem(globalKey);
      if (globalStored) {
        return JSON.parse(globalStored);
      }
    } catch (error) {
      // Error getting from localStorage
    }
    return null;
  }

  // Get current rounding settings observable
  getCurrentRoundingSettings$(): Observable<RoundingSettings | null> {
    return this.currentRoundingSettings$.asObservable();
  }

  // Get current rounding settings synchronously
  getCurrentRoundingSettings(): RoundingSettings | null {
    return this.currentRoundingSettings$.value;
  }

  /** ngx-mask pattern for unit prices (same as sales/purchase invoices). */
  getNgxMaskSeparatorPattern(): string {
    const settings = this.getCurrentRoundingSettings();
    const displayPrecision = settings?.displayPrecision ?? 2;
    return `separator.${displayPrecision}`;
  }

  // Clear rounding settings from localStorage
  clearRoundingSettingsFromStorage(): void {
    try {
      const tenantId = this.getCurrentTenantId();
      const tenantKey = `${tenantId}_${this.ROUNDING_SETTINGS_KEY}`;
      localStorage.removeItem(tenantKey);
      this.currentRoundingSettings$.next(null);
      // Broadcast to other tabs
      this.broadcastSettingsChange(null as any);
    } catch (error) {
      console.error('Error clearing rounding settings from localStorage:', error);
    }
  }

  // Initialize from localStorage on service startup
  private initializeFromStorage(): void {
    const storedSettings = this.getRoundingSettingsFromStorage();
    if (storedSettings) {
      this.currentRoundingSettings$.next(storedSettings);
    }
  }

  // Setup cross-tab synchronization
  private setupCrossTabSync(): void {
    const tenantId = this.getCurrentTenantId();
    const tenantKey = `${tenantId}_${this.ROUNDING_SETTINGS_KEY}`;

    // Listen for storage events from other tabs
    window.addEventListener('storage', (event) => {
      if (event.key === tenantKey && event.newValue) {
        try {
          const settings = JSON.parse(event.newValue);
          this.currentRoundingSettings$.next(settings);
          console.log('Rounding settings updated from another tab:', settings);
        } catch (error) {
          console.error('Error parsing rounding settings from storage event:', error);
        }
      }
    });

    // Listen for custom events for same-tab communication
    window.addEventListener('roundingSettingsChanged', (event: any) => {
      if (event.detail && event.detail !== this.currentRoundingSettings$.value) {
        this.currentRoundingSettings$.next(event.detail);
        console.log('Rounding settings updated from same tab:', event.detail);
      }
    });
  }

  // Broadcast settings change to other tabs
  private broadcastSettingsChange(settings: RoundingSettings): void {
    // Dispatch custom event for same-tab communication
    const event = new CustomEvent('roundingSettingsChanged', {
      detail: settings
    });
    window.dispatchEvent(event);
  }

  // Method to manually trigger settings update (useful for testing)
  public triggerSettingsUpdate(settings: RoundingSettings): void {
    this.currentRoundingSettings$.next(settings);
    this.broadcastSettingsChange(settings);
  }

  updateRoundingConfiguration(
    basedOnType: BasedOnType,
    basedOnId: number,
    payload: { roundingSettings: Partial<RoundingSettings> },
  ): Observable<RoundingConfigurationResponse> {
    const roundingSettings = this.applyLockedRoundingDefaults(
      payload.roundingSettings,
    );
    return this.http.put<RoundingConfigurationResponse>(
      `${this.baseUrl}/Setting/rounding/${basedOnType}/${basedOnId}`,
      { roundingSettings },
    );
  }

  // Static data fetch functions for dropdowns
  listRoundingPolicies = (pageNumber = 1, pageSize = 10, term = '', advancedFilter: any[] = []): Observable<any> => {
    const options = [
      { id: RoundingPolicy.Nearest, nameAr: 'الأقرب', nameEn: 'Nearest' },
      { id: RoundingPolicy.Up, nameAr: 'تقريب لأعلى', nameEn: 'Round Up' },
      { id: RoundingPolicy.Down, nameAr: 'تقريب لأسفل', nameEn: 'Round Down' }
    ];

    return of({
      data: options,
      totalCount: options.length,
      pageNumber: 1,
      pageSize: options.length
    });
  }

  getRoundingPolicyById = (id: string): Observable<any> => {
    const options = [
      { id: RoundingPolicy.Nearest, nameAr: 'الأقرب', nameEn: 'Nearest' },
      { id: RoundingPolicy.Up, nameAr: 'تقريب لأعلى', nameEn: 'Round Up' },
      { id: RoundingPolicy.Down, nameAr: 'تقريب لأسفل', nameEn: 'Round Down' }
    ];

    const option = options.find(opt => opt.id === id);
    return of(option);
  }

  listAggregationRoundingPolicies = (pageNumber = 1, pageSize = 10, term = '', advancedFilter: any[] = []): Observable<any> => {
    const options = [
      { id: AggregationRoundingPolicy.AggregateThenRound, nameAr: 'تجميع ثم تقريب', nameEn: 'Aggregate Then Round' },
      { id: AggregationRoundingPolicy.RoundThenAggregate, nameAr: 'تقريب ثم تجميع', nameEn: 'Round Then Aggregate' }
    ];

    return of({
      data: options,
      totalCount: options.length,
      pageNumber: 1,
      pageSize: options.length
    });
  }

  getAggregationRoundingPolicyById = (id: string): Observable<any> => {
    const options = [
      { id: AggregationRoundingPolicy.AggregateThenRound, nameAr: 'تجميع ثم تقريب', nameEn: 'Aggregate Then Round' },
      { id: AggregationRoundingPolicy.RoundThenAggregate, nameAr: 'تقريب ثم تجميع', nameEn: 'Round Then Aggregate' }
    ];

    const option = options.find(opt => opt.id === id);
    return of(option);
  }
}
